<template>
    <div v-if="false == loading">
        <div v-if="presenter.photo" class="presenter-image">
            <img class="presenter-graphic" :src="presenter.photo.uri" :alt="presenter.display_name + ' photo'" />
            <span v-if="presenter.photo.credit" class="photo-credit"><br />{{ presenter.photo.credit }}</span>
        </div>
        <h1 tabindex="-1" v-show="false == loading" ref="mainHeading" class="invisible-focus">{{ documentTitle }}</h1>
        <div v-if="errorMessage && '' !== errorMessage"><span class="dev-output">{{ errorMessage }}</span></div>
        <div class="additional-personal-info" v-if="presenter.professional_title">
            {{ presenter.professional_title }}
        </div>
        <div class="additional-personal-info" v-if="presenter.employer">
            {{ presenter.employer.name }}
        </div>
        <div class="additional-personal-info" v-if="presenter.publication_affiliation">{{ presenter.publication_affiliation }}</div>
        <div v-html="presenter.bio"></div>
        <div v-html="presenter.disclosures"></div>
        <p>
            <a :href="'mailto:' + presenter.publication_email_address">{{ presenter.publication_email_address }}</a>
        </p>
        <div v-if="presenter.publication_url || 0 < presenter.social_media.length">
            <span v-if="presenter.publication_url" class="social-media-icon">
                <a :href="presenter.publication_url" target="_new"><span class="fa fa-solid fa-globe">Website</span></a>
            </span>
            <span v-for="socialMedium in presenter.social_media" :key="socialMedium.id" class="social-media-icon">
                <a :href="socialMedium.relationship_url" target="_new"><span :class="socialMedium.icon_name">{{ socialMedium.name }}</span></a>
            </span>
        </div>
    </div>
    <SkeletonPage v-if="loading" bones="presenter, presenter-header, presenter-title, presenter-bio, presenter-schedule" />
    <div v-if="false == loading && hasSessions" :key="renderkey" class="session-list">
        <div v-for="eventArray in eventArrays" :key="eventArray.event.id" ref="sessiondisplay">
            <div class="row ">
                <div class="col event-separator">
                  <router-link :to="{name: 'scheduleview', params: {eventId: eventArray.event.id}}">
                    {{ eventArray.event.name }}
                  </router-link>
                </div>
            </div>
            <SessionList :sessions="eventArray.sessions" :showSessionButtons="false"
                @session-added="sessionAddedHandler" @session-removed="sessionRemovedHandler" @presenterIdChanged="presenterIdChangedHandler" />
        </div>
    </div>
</template>

<script>
import {callApi} from '@/shared/ApiHelper'
import {calculateCacheExpiration} from '@/shared/DateHelper'
import {checkForSignIn} from '@/shared/susiHelper'
import dayjs from 'dayjs'
import {markMandatorySessions} from '@/shared/SessionHelper'
import SessionList from '@/components/SessionList'
import {sessionsByAttendee} from '@/shared/SessionHelper'
import {sessionsSortByEvents} from '@/shared/Utilities'
import SkeletonPage from '@/components/SkeletonPage'
import {useApiStore} from '@/store/api.js'

export default {
    name: "PresenterView",

    components: {
        SessionList,
        SkeletonPage,
    },

    computed: {
        hasSessions() {
            return 0 < this.sessions.length
        }
    },

    async created() {
        await this.onCreated()
    },

    data() {
        return {
            apiStore: useApiStore(),
            documentTitle: 'Presenter',
            errorMessage: null,
            eventArrays: null,
            isAuthenticated: false,
            loading: true,
            presenter: {},
            renderkey: 0,
            sessions: [],
        }
    },

    methods: {
        async onCreated(presenterId = null) {
            try {
                checkForSignIn(this.apiStore.baseUrl)
                this.isAuthenticated = null != this.apiStore.oid

                let presenter = null
                if (null == presenterId) {
                    presenter = this.apiStore.currentPresenter
                    presenterId = this.$route.params.presenterId
                }
                if (null == presenter || presenterId != presenter.presenter.id || dayjs() > presenter.expires) {
                    this.loading = true
                    let apiResponse = await callApi(this.apiStore.buildUrl('/events/presenters/' + presenterId + '/'))
                    if (apiResponse.data) {
                        this.presenter = apiResponse.data

                        apiResponse = await callApi(this.apiStore.buildUrl('/events/sessions/?presenter=' + presenterId))
                        const sessions = apiResponse.data
                        if (this.isAuthenticated) {
                            const attendeeSessions = await sessionsByAttendee(this.apiStore.oid)
                            for (let i = 0; i < sessions.length; ++i) {
                                sessions[i].attending = false
                                for (let j = 0; j < attendeeSessions.length; ++j) {
                                    if (attendeeSessions[j].id == sessions[i].id) {
                                        sessions[i].attending = true
                                    }
                                }
                            }
                            await markMandatorySessions(this.apiStore.oid, sessions)
                        } else {
                            for (let i = 0; i < sessions.length; ++i) {
                                sessions[i].attending = false
                                sessions[i].mandatory = false
                            }
                        }

                        this.sessions = sessions.filter(
                            function(item){
                                return item.is_published == true;
                            }
                        )
                        this.eventArrays = sessionsSortByEvents(sessions)

                        const expires = calculateCacheExpiration()
                        this.apiStore.currentPresenter = {expires: expires, presenter: this.presenter, sessions: this.sessions}
                    }

                } else {
                    this.presenter = presenter.presenter
                    this.sessions = presenter.sessions
                    this.eventArrays = sessionsSortByEvents(this.sessions)
                }

                if (this.presenter.display_name) {
                    this.documentTitle = this.presenter.display_name
                    if (this.$refs.mainHeading) {
                        this.$refs.mainHeading.focus()
                    }
                }

            } catch (exception) {
                const errorMessage = exception.stack
                if ('TypeError: ' == errorMessage.substring(0, 11)) {
                    console.error(errorMessage)
                } else {
                    this.errorMessage = errorMessage
                }
            } finally {
                this.loading = false
                document.title = this.documentTitle
            }
        },

        async presenterIdChangedHandler(value) {
            console.log('PresenterView changing to presenter ID ' + value)
            await this.onCreated(value)
        },

        sessionAddedHandler(value) {
            let i = 0
            while(this.sessions[i].id != value) {
                ++i
            }
            this.sessions[i].attending = true
            this.renderKey++
        },

        sessionRemovedHandler(value) {
            let i = 0
            while(this.sessions[i].id != value) {
                ++i
            }
            this.sessions[i].attending = false
            this.renderKey++
        },
    },
}
</script>

<style scoped>

h1, h3 {
    margin-bottom: 0.2em;
}

.photo-credit {
    font-size: 80%;
    font-style: italic;
    color: black;
}

.session-list {
    clear: both;
    margin-top: 0.3em;
}


.social-media-icon {
    font-size: 1.3rem;
}

@media only screen and (min-width: 600px) {
    img.presenter-graphic {
        max-width: 300px !important;
        height: auto;
    }

    .presenter-image {
        float: left;
        margin: 0.1em 0.3em 0.05em 0em;
    }
}

</style>
